import React, { MouseEvent, useMemo, useState } from 'react';
import { classNames } from 'primereact/utils';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Box, BoxSection } from 'components/_new/Box';
import { Icon } from 'components/_new/Icon';
import { Line } from 'components/_new/Line';
import { usePanel } from 'contexts/panelContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Pair } from 'types/Pair';
import { userDisplayName } from 'utils/userDisplayName';
import { useKnowledgeBaseArticleLink } from 'components/knowledgeBase/KnowledgeBaseLink';
import { TimelineList } from 'components/_common/timeline/TimelineList';
import { Dialog } from 'components/_new/Dialog';
import { Skeleton } from 'primereact/skeleton';

type QuickLinksPairProps = {
	pair: Pair;
	onNavigate?: () => void;
};

const QuickLinksPair = ({ pair, onNavigate }: QuickLinksPairProps) => {
	const { t } = useTranslation();
	const { panelScopePath, todosData } = usePanel();

	const pairTodos =
		todosData.pair.find((pairTodo) => pairTodo.items.some((item) => item.variables?.pairId === pair.id))?.items ||
		[];

	const hasContractAlert = pairTodos.some((todo) => todo.type === 'contract_initial_sign_required');
	const hasMenteePlanAlert = pairTodos.some((todo) => todo.type === 'contract_goals_sign_required');
	const hasSessionAlert = pairTodos.some((todo) => todo.type === 'arrange_first_session_date');

	const warningBorderStyle = {
		borderColor: '#F674CB',
	};

	return (
		<div className="flex flex-row flex-wrap gap-2">
			<Link to={{ pathname: `${panelScopePath}/pair/${pair.id}/session` }} className="flex-1">
				<Box
					className="relative h-full"
					variant="white-bordered"
					slim
					full
					style={hasSessionAlert ? warningBorderStyle : undefined}
					onClick={onNavigate}
				>
					<BoxSection contentClassName="align-items-center">
						<Icon name="calendar-check" iconStyle="regular" className="text-rose fa-2x" />
						<span>{t('userPanel.navigation.sessions')}</span>
					</BoxSection>
					{hasSessionAlert && (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					)}
				</Box>
			</Link>
			<Link to={{ pathname: `${panelScopePath}/pair/${pair.id}/contract` }} className="flex-1">
				<Box
					className="relative h-full"
					variant="white-bordered"
					slim
					full
					style={hasContractAlert ? warningBorderStyle : undefined}
					onClick={onNavigate}
				>
					<BoxSection contentClassName="align-items-center">
						<Icon name="file-signature" iconStyle="solid" className="text-primary fa-2x" />
						<span>{t('userPanel.navigation.contract')}</span>
					</BoxSection>
					{hasContractAlert && (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					)}
				</Box>
			</Link>
			<Link to={{ pathname: `${panelScopePath}/pair/${pair.id}/mentee-plan` }} className="flex-1">
				<Box
					className="relative h-full"
					variant="white-bordered"
					slim
					full
					style={hasMenteePlanAlert ? warningBorderStyle : undefined}
					onClick={onNavigate}
				>
					<BoxSection contentClassName="align-items-center">
						<Icon name="list" iconStyle="solid" className="text-primary fa-2x" />
						<span>{t('userPanel.dashboard.quickLinks.developmentPlan')}</span>
					</BoxSection>
					{hasMenteePlanAlert && (
						<div className="box-warning-icon">
							<Icon name="exclamation-circle" className="warning-icon-color" />
						</div>
					)}
				</Box>
			</Link>
		</div>
	);
};

type QuickLinksProps = {
	onNavigate?: () => void;
	className?: string;
};

type QuickLinksPairsSectionProps = {
	onNavigate?: () => void;
	className?: string;
};

export const QuickLinksPairsSection = ({ onNavigate, className }: QuickLinksPairsSectionProps) => {
	const { t } = useTranslation();
	const { panelScopePath, userPairs } = usePanel();

	return (
		<div className={classNames('flex flex-column gap-2', className)}>
			{userPairs && userPairs.length === 0 ? (
				<Link to={{ pathname: `${panelScopePath}/pairing` }} className="flex-1" onClick={onNavigate}>
					<Box className="h-full" variant="white-bordered" slim full>
						<BoxSection contentClassName="align-items-center">
							<Icon name="people-arrows" iconStyle="solid" className="text-rose fa-2x" />
							<span>{t('userPanel.dashboard.quickLinks.pairing')}</span>
						</BoxSection>
					</Box>
				</Link>
			) : null}
			{userPairs && userPairs.length === 1 ? (
				<QuickLinksPair pair={userPairs[0]} onNavigate={onNavigate} />
			) : null}
			{userPairs && userPairs.length > 1 ? (
				<>
					{userPairs.map((pair) => (
						<div key={pair.id} className="flex flex-column gap-1">
							<div className="px-2">
								{t('userPanel.dashboard.quickLinks.processWith', {
									name: userDisplayName(pair.application.programMembership.user),
								})}
							</div>
							<QuickLinksPair pair={pair} onNavigate={onNavigate} />
						</div>
					))}
					<Line />
				</>
			) : null}
		</div>
	);
};

const QuickLinksFooterSection = ({ onNavigate }: Pick<QuickLinksProps, 'onNavigate'>) => {
	const { t } = useTranslation();
	const { panelScopePath, currentProgram } = usePanel();
	const timelineEnabled = Boolean(currentProgram?.eventsEnabled);

	const { link } = useKnowledgeBaseArticleLink('faq');
	const faqLink = useMemo(() => {
		return link?.to.pathname;
	}, [link]);

	const [showCalendarModalOpen, setShowCalendarModalOpen] = useState(false);
	const handleTimelineClick = (event: MouseEvent<HTMLAnchorElement>) => {
		setShowCalendarModalOpen(true);
		onNavigate?.();
		event.preventDefault();
	};

	return (
		<>
			<Dialog
				title={t('timeline.title')}
				visible={showCalendarModalOpen}
				onHide={() => {
					setShowCalendarModalOpen(false);
				}}
				size="lg"
			>
				<TimelineList dialogMode />
			</Dialog>

			<div className="flex flex-row flex-wrap gap-2">
				<Link to={{ pathname: `${panelScopePath}/knowledge-base` }} className="flex-1" onClick={onNavigate}>
					<Box className="h-full" variant="white-bordered" slim full>
						<BoxSection horizontal contentClassName="justify-content-center align-items-center">
							<Icon name="book" iconStyle="solid" className="text-primary fa-2x" />
							<span>{t('knowledgeBase.title')}</span>
						</BoxSection>
					</Box>
				</Link>
				{timelineEnabled && (
					<Link
						to={{ pathname: `${panelScopePath}/timeline` }}
						className="flex-1"
						onClick={handleTimelineClick}
					>
						<Box className="h-full" variant="white-bordered" slim full>
							<BoxSection horizontal contentClassName="justify-content-center align-items-center">
								<Icon name="calendar" className="text-primary fa-2x" />
								<span>{t('timeline.title')}</span>
							</BoxSection>
						</Box>
					</Link>
				)}
				{faqLink && (
					<Link to={{ pathname: `${panelScopePath}/${faqLink}` }} className="flex-1" onClick={onNavigate}>
						<Box className="h-full" variant="white-bordered" slim full>
							<BoxSection horizontal contentClassName="justify-content-center align-items-center">
								<Icon name="circle-question" iconStyle="regular" className="text-primary fa-2x" />
								<span>FAQ</span>
							</BoxSection>
						</Box>
					</Link>
				)}
			</div>
		</>
	);
};

export const QuickLinks = ({ onNavigate, className }: QuickLinksProps) => {
	const { t } = useTranslation();

	return (
		<>
			<Skeleton className="w-full h-1" />
			<div className={classNames('flex flex-column gap-2 lg:hidden mb-4', className)}>
				<SubPageTitle title={t('userPanel.dashboard.quickLinks.title')} />
				<QuickLinksPairsSection onNavigate={onNavigate} />
				<QuickLinksFooterSection onNavigate={onNavigate} />
			</div>
		</>
	);
};
